import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AcademicCapIcon, CalendarDaysIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import StudentAccountMenu from '../../../shared/components/StudentAccountMenu';
import { getGradeBand } from '../../../shared/constant/grading';
import { TERMS } from '../../../shared/constant/terms';
import { useOwnStudentRecord } from '../hooks/useOwnStudentRecord';
import StudentAlerts from './StudentAlerts';
import StudentAttendanceView from './StudentAttendanceView';

const TABS = ['All', 'Core', 'Applied', 'Specialized'];

// The dashboard's two views. On mobile they're the bottom nav; from the `sm` breakpoint up they
// become tabs under the student's name instead.
const VIEWS = [
  { key: 'grades', label: 'Grades', title: 'My Grades', icon: AcademicCapIcon },
  { key: 'attendance', label: 'Attendance', title: 'My Attendance', icon: CalendarDaysIcon },
];

function StudentDashboardPage() {
  const [activeTab, setActiveTab] = useState('All');
  const [view, setView] = useState(VIEWS[0].key);
  const scrollRef = useRef(null);
  const [term, setTerm] = useState(String(TERMS[0]));
  const { student, subjects, grades, attendance, attendanceError, attentionReviews, loading, error } =
    useOwnStudentRecord();
  const isSecretary = Boolean(student?.is_secretary);

  // The term grade of a subject, or null until the student has one for the selected term.
  const gradeOf = (subject) => grades[`${subject.subjectId}|${term}`] ?? null;

  // The general average counts only the subjects graded so far in the selected term.
  const generalAverage = useMemo(() => {
    const termGrades = subjects.map((s) => grades[`${s.subjectId}|${term}`]).filter((g) => g != null);
    return termGrades.length ? termGrades.reduce((sum, g) => sum + g, 0) / termGrades.length : null;
  }, [subjects, grades, term]);
  const averageBand = getGradeBand(generalAverage);

  const filteredSubjects = useMemo(
    () => (activeTab === 'All' ? subjects : subjects.filter((s) => s.category === activeTab)),
    [activeTab, subjects]
  );

  // The scroll box is its own element, so switching views would otherwise keep the old scroll position.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [view]);

  return (
    <div className="h-dvh w-full flex flex-col bg-appbg overflow-hidden">
      <header className="w-full shrink-0 h-20 bg-[#0b4a71] border-b border-gray-200 px-4 flex items-center gap-3">
        <h1 className="flex-1 text-xl font-semibold text-white">{VIEWS.find((v) => v.key === view).title}</h1>
        <StudentAccountMenu />
      </header>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        <main className="w-full p-4 lg:w-3/4 lg:mx-auto">
          {loading ? (
            <p className="w-full text-center text-gray-500 py-12">Loading…</p>
          ) : error ? (
            <p className="w-full text-center text-red-600 py-12">{error}</p>
          ) : (
            <div className="w-full space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-semibold text-[#0b4a71] uppercase">
                    {student.lastname}, {student.firstname}
                  </h2>
                  <p className="text-md text-gray-700">LRN: {student.lrn}</p>
                  <p className="text-md text-gray-500">
                    Grade {student.grade_level} - {student.section}
                  </p>
                </div>

                <select
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  aria-label="Term"
                  className="shrink-0 rounded-md border border-[#4ea6d0] bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                >
                  {TERMS.map((t) => (
                    <option key={t} value={t}>Term {t}</option>
                  ))}
                </select>
              </div>

              {isSecretary && (
                <Link
                  to="/student-dashboard/secretary"
                  className="flex items-center gap-2 rounded-lg border border-[#4ea6d0] bg-[#4ea6d0]/10 px-4 py-3 text-sm font-medium text-[#0b4a71] hover:bg-[#4ea6d0]/20"
                >
                  <ClipboardDocumentCheckIcon className="h-5 w-5 shrink-0" />
                  You're the class secretary — check or update your section's attendance
                </Link>
              )}

              <div className="hidden sm:flex gap-2">
                {VIEWS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setView(key)}
                    aria-pressed={view === key}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium border transition ${
                      view === key
                        ? 'bg-[#0b4a71] border-[#0b4a71] text-white'
                        : 'border-gray-300 text-gray-600 hover:border-[#4ea6d0] hover:text-[#0b4a71]'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {view === 'attendance' ? (
                <StudentAttendanceView
                  subjects={subjects}
                  attendance={attendance}
                  attendanceError={attendanceError}
                  term={term}
                />
              ) : (
                <>
                  <StudentAlerts
                    subjects={subjects}
                    grades={grades}
                    attendance={attendance}
                    attentionReviews={attentionReviews}
                    term={term}
                  />

                  <div className="bg-[#0b4a71] rounded-lg p-5 text-center">
                    <p className="text-sm text-white/70">General Average</p>
                    <p className="text-4xl font-bold text-white">{generalAverage == null ? '—' : generalAverage.toFixed(2)}</p>
                    {averageBand && (
                      <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${averageBand.tone}`}>
                        {averageBand.remark}
                      </span>
                    )}
                  </div>

                  <div className="flex gap-2 overflow-x-auto">
                    {TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveTab(tab)}
                        className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium border transition ${
                          activeTab === tab
                            ? 'bg-[#4ea6d0] border-[#4ea6d0] text-white'
                            : 'border-gray-300 text-gray-600 hover:border-[#4ea6d0] hover:text-[#0b4a71]'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-3">
                    {filteredSubjects.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">
                        {subjects.length === 0
                          ? 'No subjects are assigned to your section yet.'
                          : 'No subjects found for this filter.'}
                      </p>
                    ) : (
                      filteredSubjects.map((subject) => {
                        const grade = gradeOf(subject);
                        const band = getGradeBand(grade);
                        return (
                          <Link
                            key={subject.subjectId}
                            to={`/student-dashboard/subjects/${subject.subjectId}`}
                            className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between gap-3 hover:border-[#4ea6d0]"
                          >
                            <div className="min-w-0">
                              <p className="font-semibold text-[#0b4a71] truncate">{subject.name}</p>
                              <p className="text-xs text-gray-500">{subject.category} - {subject.teacher}</p>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-2xl font-bold text-[#0b4a71]">{grade ?? '—'}</p>
                              {band && (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${band.tone}`}>
                                  {band.remark}
                                </span>
                              )}
                            </div>
                          </Link>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </main>
      </div>

      {student && (
        <nav
          aria-label="Student sections"
          className="sm:hidden shrink-0 bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)]"
        >
          <div className="grid grid-cols-2">
            {VIEWS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                aria-current={view === key ? 'page' : undefined}
                className={`flex flex-col items-center gap-0.5 py-2 text-xs font-medium transition ${
                  view === key ? 'text-[#0b4a71] bg-[#4ea6d0]/10' : 'text-gray-500'
                }`}
              >
                <Icon className="h-6 w-6" />
                {label}
              </button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

export default StudentDashboardPage;
